import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { Link, type LinkProps } from 'react-router'
import { cn } from '@/lib/utils'

const button = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-fg hover:opacity-90',
        brand: 'bg-brand text-ink hover:brightness-105',
        secondary: 'bg-surface-3 text-text hover:bg-border',
        outline: 'border border-border bg-surface text-text hover:bg-surface-2',
        ghost: 'text-text-2 hover:bg-surface-3 hover:text-text',
        danger: 'bg-danger text-white hover:opacity-90',
        'danger-outline': 'border border-danger/40 text-danger hover:bg-danger-soft',
        link: 'text-text underline-offset-4 hover:underline px-0',
      },
      size: {
        sm: 'h-8 px-3 text-[13px]',
        md: 'h-9 px-4 text-sm',
        lg: 'h-11 px-5 text-[15px]',
        icon: 'size-9',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, loading, children, disabled, type = 'button', ...props }, ref) => (
  <button ref={ref} type={type} className={cn(button({ variant, size }), className)} disabled={disabled || loading} {...props}>
    {loading && <Loader2 className="animate-spin" />}
    {children}
  </button>
))
Button.displayName = 'Button'

export { button as buttonVariants }


/** A link that looks like a button. */
export function LinkButton({ className, variant, size, ...props }: LinkProps & VariantProps<typeof button>) {
  return <Link className={cn(button({ variant, size }), className)} {...props} />
}
